/**
 * Financial-health scores on [1, 5] (ADR-0034). Missing inputs are omitted
 * from the average — they are not scored as a neutral 3 (R2b).
 */
import type { StockRatios } from '@meridian/contracts';

export interface RatingScore {
  readonly metric: string;
  readonly score: number;
  readonly raw: number;
}

export interface RatingsSnapshot {
  readonly scores: readonly RatingScore[];
  /** Null when fewer than 3 metrics are scorable. */
  readonly average: number | null;
}

function scorePe(pe: number): number {
  if (pe < 10) return 5;
  if (pe < 15) return 4;
  if (pe < 25) return 3;
  if (pe < 40) return 2;
  return 1;
}

function scorePb(pb: number): number {
  if (pb <= 0) return 1;
  if (pb < 1) return 5;
  if (pb < 3) return 4;
  if (pb < 5) return 3;
  if (pb < 10) return 2;
  return 1;
}

function scoreRoe(roe: number): number {
  const pct = roe * 100;
  if (pct > 25) return 5;
  if (pct > 15) return 4;
  if (pct > 8) return 3;
  if (pct > 0) return 2;
  return 1;
}

function scoreRoa(roa: number): number {
  const pct = roa * 100;
  if (pct > 15) return 5;
  if (pct > 8) return 4;
  if (pct > 3) return 3;
  if (pct > 0) return 2;
  return 1;
}

function scoreDe(de: number): number {
  if (de < 0) return 1;
  if (de < 0.5) return 5;
  if (de < 1) return 4;
  if (de < 2) return 3;
  if (de < 4) return 2;
  return 1;
}

function scoreCurrent(cr: number): number {
  if (cr > 2) return 5;
  if (cr > 1.5) return 4;
  if (cr > 1) return 3;
  if (cr > 0.5) return 2;
  return 1;
}

function scored(
  metric: string,
  raw: number | null,
  score: (value: number) => number,
): RatingScore | null {
  if (raw === null || !Number.isFinite(raw)) return null;
  return { metric, score: score(raw), raw };
}

const MIN_SCORES = 3;

export function ratingsSnapshot(ratios: StockRatios, peTtm: number | null): RatingsSnapshot {
  const scores = [
    scored('P/E', peTtm !== null && peTtm > 0 ? peTtm : null, scorePe),
    scored('P/B', ratios.pb, scorePb),
    scored('ROE', ratios.roe, scoreRoe),
    scored('ROA', ratios.roa, scoreRoa),
    scored('D/E', ratios.de, scoreDe),
    scored('Liquidity', ratios.currentRatio, scoreCurrent),
  ].filter((row): row is RatingScore => row !== null);
  if (scores.length < MIN_SCORES) return { scores, average: null };
  const average = scores.reduce((sum, row) => sum + row.score, 0) / scores.length;
  return { scores, average: Number.isFinite(average) ? average : null };
}
